import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BookOpen, Bookmark, Download, Clock, ArrowRight } from "lucide-react";

export default function MyLibrary() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/library/saved").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
      <div className="mb-6">
        <div className="overline text-accent">/ MY LIBRARY</div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-2" data-testid="my-library-title">
          Saved resources
        </h1>
        <p className="text-sm text-muted-foreground mt-2">Everything you&rsquo;ve bookmarked from the public Excellence Library.</p>
        <div className="mt-3">
          <Link to="/library" className="text-sm text-primary hover:underline inline-flex items-center gap-1 font-mono-plex uppercase tracking-widest">
            → Browse the full library
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm p-16 text-center bg-card/40">
          <Bookmark className="w-8 h-8 mx-auto text-muted-foreground" />
          <div className="font-display text-lg mt-4">Nothing saved yet</div>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Head to the library and click the Save icon on any resource — it&rsquo;ll show up here for quick access.
          </p>
          <Link to="/library">
            <Button className="mt-6 rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="my-library-browse-btn">
              Browse the library <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((r) => (
            <Link key={r.slug} to={`/library/${r.slug}`} className="group" data-testid={`saved-${r.slug}`}>
              <div className="h-full border border-border bg-card rounded-sm hover:border-primary hover:shadow-lg transition-all overflow-hidden">
                <div className="h-24 bg-gradient-to-br from-primary/20 via-primary/5 to-accent/10 flex items-center justify-center border-b border-border">
                  <BookOpen className="w-8 h-8 text-primary/40" />
                </div>
                <div className="p-5">
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    <Badge className="rounded-sm font-mono-plex text-[10px] uppercase tracking-widest bg-primary/10 text-primary hover:bg-primary/10">{r.resource_type}</Badge>
                    <Badge className="rounded-sm font-mono-plex text-[10px] uppercase tracking-widest bg-secondary">{r.skill_level}</Badge>
                  </div>
                  <div className="font-display font-bold text-base leading-tight group-hover:text-primary transition-colors">{r.title}</div>
                  <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{r.description}</p>
                  <div className="mt-3 pt-2 border-t border-border flex items-center justify-between text-[10px] font-mono-plex uppercase tracking-widest text-muted-foreground">
                    {r.time_saved && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {r.time_saved}</span>}
                    <span className="inline-flex items-center gap-1"><Download className="w-3 h-3" /> {r.downloads_count || 0}</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
